import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Heart, Share2, Flag, MapPin, Zap, ShieldCheck, BadgeCheck } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuthGate } from "@/components/AuthGate";
import { Button } from "@/components/ui/button";
import { ReportDialog, ShareDialog, CheckoutDialog } from "@/components/dialogs";
import { VerifBadge, PageLoading, DemoTag, SafetyBanner, BlockedScreen } from "@/components/common";
import { ManageMenu, ListingStatusBadge } from "@/components/ManageListing";

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const gate = useAuthGate();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [saved, setSaved] = useState(false);
  const [report, setReport] = useState(false);
  const [share, setShare] = useState(false);
  const [boost, setBoost] = useState(false);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    api.get(`/marketplace/${id}`).then(({ data }) => { setData(data); setSaved(!!data.saved); }).catch((e) => { if (e?.response?.status === 403) setData("blocked"); });
    api.get("/payments/products").then(({ data }) => setProduct(data.items.find((p) => p.lookup_key === "marketplace_boost")));
  }, [id]);
  const reload = () => api.get(`/marketplace/${id}`).then(({ data }) => { setData(data); setSaved(!!data.saved); }).catch(() => {});

  if (data === "blocked") return <BlockedScreen onBack={() => nav(-1)} />;
  if (!data) return <PageLoading />;
  const l = data.listing;
  const seller = data.seller;
  const isOwner = user && l.owner_id === user.id;

  const save = async () => {
    if (!gate.require("Create a free account to save listings and connect with the community.")) return;
    try {
      if (saved) {
        await api.delete("/favourites", { params: { item_type: "marketplace", item_id: id } });
        setSaved(false);
        toast.success("Removed from Saved");
      } else {
        await api.post("/favourites", { item_type: "marketplace", item_id: id });
        setSaved(true);
        toast.success("Saved");
      }
    } catch (e) { toast.error(apiError(e)); }
  };
  const message = async () => {
    if (!gate.require("Create a free account to message sellers on Mtaa.")) return;
    if (isOwner) return toast.info("This is your listing.");
    try { const { data } = await api.post("/conversations", { recipient_id: l.owner_id, context_type: "marketplace", context_id: id, context_label: l.title, message: `Hi, is "${l.title}" still available?` }); nav(`/messages/${data.conversation_id}`); } catch (e) { toast.error(apiError(e)); }
  };
  const photos = l.photos?.length ? l.photos : [null];

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <div className="relative aspect-square overflow-hidden rounded-2xl border border-border bg-muted">
            <img src={fileUrl(photos[idx]) || "https://images.pexels.com/photos/13801642/pexels-photo-13801642.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"} alt={l.title} className="h-full w-full object-cover" />
            {l.urgent && <span className="absolute left-3 top-3 inline-flex items-center gap-0.5 rounded-md bg-accent px-2 py-0.5 text-xs font-bold text-white"><Zap className="h-3 w-3" />URGENT</span>}
            {l.is_demo && <span className="absolute right-3 top-3"><DemoTag /></span>}
          </div>
          {photos.length > 1 && (
            <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
              {photos.map((p, i) => <button key={p} onClick={() => setIdx(i)} className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border ${i === idx ? "border-primary" : "border-border"}`}><img src={fileUrl(p)} alt="" className="h-full w-full object-cover" /></button>)}
            </div>
          )}
        </div>
        <div className="space-y-3">
          <p className="font-display text-3xl font-extrabold text-primary">{l.price > 0 ? `$${l.price.toLocaleString()}` : "Free"} {l.negotiable && <span className="text-sm font-medium text-muted-foreground">ONO</span>}</p>
          <div className="flex items-center gap-2"><h1 className="font-display text-xl font-bold">{l.title}</h1>{isOwner && l.status !== "active" && <ListingStatusBadge status={l.status} />}</div>
          <p className="flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{l.suburb ? `${l.suburb}, ` : ""}{l.state} · {l.condition}</p>
          {l.description && <p className="text-sm leading-relaxed text-foreground/80">{l.description}</p>}
          {seller && (
            <Link to={`/u/${seller.id}`} data-testid="listing-seller-link" className="flex items-center gap-2 rounded-xl border border-border p-3 hover:border-primary">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary font-semibold text-primary">{(seller.name || "U")[0]}</div>
              <div className="flex-1">
                <p className="text-sm font-medium">{seller.name}</p>
                <div className="flex gap-1">{seller.phone_verified && <VerifBadge type="phone_verified" />}{seller.identity_verified && <VerifBadge type="identity_verified" />}</div>
              </div>
            </Link>
          )}
          <div className="flex flex-col gap-2">
            {isOwner ? (
              <>
                <div className="rounded-xl border border-border bg-secondary/40 p-2 text-center text-xs font-medium text-muted-foreground" data-testid="owner-notice">This is your listing — manage it below.</div>
                <ManageMenu listing={l} onChanged={reload} onDeleted={() => nav("/profile")} buttonClassName="w-full" />
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setShare(true)} data-testid="listing-share"><Share2 className="h-4 w-4" />Share</Button>
                  {!l.boosted_until && <Button variant="secondary" className="flex-1" onClick={() => setBoost(true)} data-testid="listing-boost"><Zap className="h-4 w-4" />Boost</Button>}
                </div>
              </>
            ) : (
              <>
                <Button onClick={message} data-testid="listing-message"><MessageSquare className="h-4 w-4" />Message seller</Button>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={save} data-testid="listing-save"><Heart className={saved ? "h-4 w-4 fill-accent text-accent" : "h-4 w-4"} />{saved ? "Unsave" : "Save"}</Button>
                  <Button variant="outline" className="flex-1" onClick={() => setShare(true)} data-testid="listing-share"><Share2 className="h-4 w-4" />Share</Button>
                  <Button variant="outline" onClick={() => user ? setReport(true) : gate.require("Create a free account to report listings.")} data-testid="listing-report"><Flag className="h-4 w-4" /></Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <SafetyBanner text="Meet in a safe public place. Never pay a deposit before seeing the item. Never share verification codes." />
      <ReportDialog open={report} onOpenChange={setReport} targetType="marketplace" targetId={id} reportedUserId={l.owner_id} reportedUserName={seller?.name} onBlocked={() => nav("/explore")} />
      <ShareDialog open={share} onOpenChange={setShare} url={`/listing/${id}`} title={l.title} />
      <CheckoutDialog open={boost} onOpenChange={setBoost} product={product} itemType="marketplace" itemId={id} />
    </div>
  );
}
